import { useTranslation } from 'core/i18n/i18n';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { GraphqlError } from 'core/utils/backend-service';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { settingsModulePaths } from 'core/routes/routes';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    mapEapSettingsFormModelToApiRequest,
    mapEapSettingsFormModelToUpdateApiRequest,
} from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/settings-devices-eap-settings-form/service/settings-devices-eap-settings-create-form.mapper';
import {
    createEapSetting,
    updateEapSetting,
} from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/service/eap-settings-rest.service';
import {
    EapSettingsCreateModel,
    EapSettingsFormModelValidationModel,
} from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/settings-devices-eap-settings-form/settings-devices-eap-settings-form.model';
import { mapApiErrorsToValidityErrors } from './save-eap-settings.mapper';

interface UseEapSettingSaverProps {
    isEditMode: boolean;
}

export const useEapSettingSaver = ({ isEditMode }: UseEapSettingSaverProps) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<EapSettingsFormModelValidationModel>({});
    const { t } = useTranslation();

    const notificationKey = isEditMode ? 'update' : 'create';

    const saveEapSetting = (eapSettings: EapSettingsCreateModel) => {
        setIsLoading(true);

        const request = isEditMode
            ? updateEapSetting(mapEapSettingsFormModelToUpdateApiRequest(eapSettings))
            : createEapSetting(mapEapSettingsFormModelToApiRequest(eapSettings));

        const notificationParams = isEditMode ? { name: eapSettings.name } : undefined;

        return request
            .then(() => {
                showSuccessNotification(
                    t(
                        `settings.devices.eap-settings.eap-config-create-form.notification.${notificationKey}.success`,
                        notificationParams
                    )
                );
                // Cache invalidates 5-min-stale eap queries so the list/detail show the saved data on return.
                updateQuery(settingsKeys.eapSettings());
                navigate(settingsModulePaths.eapSettings);
            })
            .catch((e) => {
                showErrorNotification(
                    t(
                        `settings.devices.eap-settings.eap-config-create-form.notification.${notificationKey}.wrong`,
                        notificationParams
                    )
                );
                const eapApiErrors = JSON.parse(e.message).errors as GraphqlError[];
                const validityErrors = mapApiErrorsToValidityErrors(eapApiErrors);
                setErrors(validityErrors);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return { isLoading, errors, saveEapSetting };
};
